import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { Filter } from '@docpath/linces-angular-components/common';
import { Table } from '@docpath/webcomponents';
import { faGauge } from '@fortawesome/free-solid-svg-icons';
import { Observable } from 'rxjs';
import { CustomStyles } from 'src/app/shared/components/sinclair-cards/model/custom-styles';

import { SinclairCard, SinclairCardFilterOption, SinclairCardFilterOptionType } from '../../shared/components/sinclair-cards/model/sinclair-card-type';
import { SinclairCardsComponent } from '../../shared/components/sinclair-cards/sinclair-cards.component';
import { Dge, DgeState } from '../configuration/engines/models/dge';
import { StatisticsHttpService } from '../statistics/services/statistics-http.service';
import { MonitoringChartComponent } from './components/chart/monitoring-chart.component';
import { MonitoringHeaderComponent } from './components/header/monitoring-header.component';
import { MonitoringTableComponent } from './components/table/monitoring-table.component';
import { MonitoringConstants } from './monitoring-constants';
import { MonitoringService } from './services/monitoring.service';


@Component({
  selector: 'app-monitoring',
  templateUrl: './monitoring.component.html',
  standalone: true,
  imports: [SinclairCardsComponent, MonitoringChartComponent, MonitoringHeaderComponent, MonitoringTableComponent]
})
export class MonitoringComponent implements OnInit, OnDestroy {

  private readonly i18nPrefix = 'monitoring';

  icon = faGauge;
  title = 'monitoring.title';

  public CARD_FILTERS_DGES = 'dges';

  sinclairCards: SinclairCard[] = [];
  dgesFilter: SinclairCardFilterOption[] = [];

  idRefreshProcess = 0;

  private table?: Table;
  filters: Filter[] = [];
  private readonly tableName = 'monitoring';

  dgeId!: number;

  private readonly monitoringService = inject(MonitoringService);
  private readonly statisticsHttpService = inject(StatisticsHttpService);

  onTableLoaded(table: Table): void {
    this.table = table;
  }

  ngOnInit(): void {

    this.statisticsHttpService.getAllDge().subscribe((dges: Dge[]) => {
      if (dges.length > 0) {
        const lastLinkedDge = dges[dges.length - 1];
        this.dgeId = lastLinkedDge.id;
        this.monitoringService.notifyDgeChange(this.dgeId);
      }
    });

    this.loadDgesFiltersOptions().subscribe(() => {
      this.loadCard();
    });

    this.monitoringService.dgeChanged.subscribe(dgeId => {
      this.dgeId = dgeId;
    });

    this.initRefreshProcess();

  }

  ngOnDestroy(): void {
    if (this.idRefreshProcess) {
      clearInterval(this.idRefreshProcess);
    }
  }

  loadCard(): void {
    this.sinclairCards = [
      new SinclairCard({
        name: this.CARD_FILTERS_DGES, rows: 12, title: this.dgesFilter.find(f => f.active)?.title as string,
        subTitle: this.dgesFilter.find(f => f.active)?.subTitle as string,
        tooltip: this.dgesFilter.find(f => f.active)?.tooltip as string,
        labelState: this.dgesFilter.find(f => f.active)?.labelState as string,
        stateClass: this.dgesFilter.find(f => f.active)?.stateClass as CustomStyles,
        filters: this.dgesFilter, filtersHeader: `${this.i18nPrefix}.cards.filters.dges.header`,
        filtersOptionTooltip: `${this.i18nPrefix}.cards.filters.dges.optionsTooltip`
      })
    ];
  }

  loadAllComponents(): void {
    const dge: number = this.sinclairCards.find(c => c.name === this.CARD_FILTERS_DGES)?.filters.find(f => f.active)?.value as number;

    this.monitoringService.notifyDgeChange(dge);
  }

  cardFiltersClicked(card: SinclairCard): void {

    if (card.name === this.CARD_FILTERS_DGES) {
      const dgeIdActive = card.filters.find(f => f.active)?.value as number;
      this.dgesFilter = [];
      this.loadDgesFiltersOptions().subscribe(() => {
        card.filters = this.dgesFilter;

        // Set active false to all filters and active true to the filter with the dgeIdActive
        card.filters.find(f => f.active === true)!.active = false;
        const filter = card.filters.find(f => f.value === dgeIdActive);
        if (filter) {
          filter.active = true;
        }
      });
    }

  }

  loadDgesFiltersOptions(): Observable<void> {
    return new Observable(observer => {
      this.statisticsHttpService.getAllDge().subscribe(result => {

        result.forEach((dge, index) => {
          const option = new SinclairCardFilterOption({
            type: SinclairCardFilterOptionType.DgeFilters,
            title: dge.name,
            subTitle: dge.dgeDescription,
            value: dge.id,
            tooltip: dge.name,
            labelState: dge.currentState,
            stateClass: { cssClass: this.getStateClassForDgeState(dge.currentState) },
            active: index === result.length - 1
          });
          this.dgesFilter.push(option);
        });

        observer.next();
        observer.complete();
      });
    });
  }

  private getStateClassForDgeState(dgeState: string): string {

    switch (dgeState) {
      case DgeState.RUNNING: return 'dpgreen';
      case DgeState.STOPPING: return 'dpyellow';
      default: return 'dpred';
    }

  }

  getTable(): Table | undefined {
    return this.table;
  }

  onFilterChange(filters: Filter[]): void {
    this.filters = filters;
  }

  private initRefreshProcess() {

    this.idRefreshProcess = setInterval(() => {
      // Refresh Charts
      this.monitoringService.notifyRefreshChange();
    }, MonitoringConstants.REFRESH_DELAY) as unknown as number;

  }

}
